import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {MatIconModule} from '@angular/material/icon';
import {
  Operand,
  Operator,
  OperatorGroups,
  OperatorType,
} from '@em/shared/mapping-engine/domain-models';
import {EmInputModule, EmSelectModule} from '@em/shared/ui';
import {TranslateModule} from '@ngx-translate/core';
import {startWith} from 'rxjs/operators';
import {EmSelectOperandModule} from '../select-operand/select-opearnd.module';

@Component({
  selector: 'em-condition',
  templateUrl: './condition.component.html',
  styleUrls: ['./condition.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatIconModule,
    EmInputModule,
    EmSelectModule,
    EmSelectOperandModule,
  ],
})
export class EmConditionComponent implements OnInit {
  private readonly _controlContainer = inject(ControlContainer);
  private readonly _destroyRef = inject(DestroyRef);

  @Input() availableAttributes: string[] | null | undefined;
  @Input() customAttributes: string[] | null | undefined;
  @Input() operatorGroups: OperatorGroups | undefined;

  conditionForm: UntypedFormGroup | null | undefined;
  requireRightOperand: boolean | undefined;
  operatorType: OperatorType | null | undefined;

  //#region Properties
  get leftOperandControl(): UntypedFormControl | null {
    return this.conditionForm?.get('leftOperand') as UntypedFormControl;
  }
  get leftOperandControlValue(): Operand | null {
    return this.conditionForm?.get('leftOperand')?.value;
  }
  get rightOperandControl(): UntypedFormControl | null {
    return this.conditionForm?.get('rightOperand') as UntypedFormControl;
  }
  get operatorControl(): UntypedFormControl | null {
    return this.conditionForm?.get('operator') as UntypedFormControl;
  }
  //#endregion

  ngOnInit(): void {
    this.conditionForm = this._controlContainer.control as UntypedFormGroup;

    this.operatorControl?.valueChanges
      .pipe(
        startWith(this.operatorControl.value),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((newOperator: string | null | undefined) => {
        this._checkRequiredRightOperand(newOperator);
        this.operatorType = this._extractOperatorType(newOperator);
        this.rightOperandControl?.setValue(this.rightOperandControl.value);
      });
  }

  getOperatorIcon(operatorName: string): string {
    const operator = this._getOperatorByName(operatorName);

    if (operator) {
      switch (operator.type) {
        case 'date':
          return 'calendar_month';
        case 'integer':
          return 'tag';
        case 'string':
          return 'text_fields';

        default:
          return '';
      }
    }

    return '';
  }

  private _getOperatorByName(operatorName: string): Operator | undefined {
    let operator: Operator | undefined;
    for (const group of this.operatorGroups || []) {
      operator = group.operators.find((op) => op.name === operatorName);
      if (operator) {
        break;
      }
    }

    return operator;
  }

  private _extractOperatorType(
    operatorName: string | null | undefined,
  ): OperatorType | null | undefined {
    if (this.operatorGroups) {
      for (const group of this.operatorGroups) {
        const operator = group.operators.find((op) => op.name === operatorName);
        if (operator) {
          return operator.type;
        }
      }
    }

    return null;
  }

  private _checkRequiredRightOperand(operatorName: string | null | undefined) {
    const requiredRightOperand = this.operatorGroups?.some((group) => {
      const operator = group.operators.find((op) => op.name === operatorName);
      return operator && operator.requireRightOperand;
    });

    this.requireRightOperand = requiredRightOperand;

    if (requiredRightOperand) {
      this.rightOperandControl?.setValidators([Validators.required]);
      this.rightOperandControl?.updateValueAndValidity();
    } else {
      if (this.rightOperandControl?.value !== null) {
        this.rightOperandControl?.setValue(null);
      }

      this.rightOperandControl?.setValidators(null);
      this.rightOperandControl?.updateValueAndValidity();
    }
  }
}
